'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: string;
  phone?: string;
  avatar_url?: string;
  job_title?: string;
  department?: string;
  is_active: boolean;
  must_change_password?: boolean;
  external_email?: string | null;
}

// Minimal stub so code that imports Session/User from supabase still compiles
type User = { id: string; email: string };
type Session = { user: User };

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const DEMO_USERS: Record<string, { password: string; profile: UserProfile }> = {
  'admin@easylawdemo.com': {
    password: 'DemoAdmin123!',
    profile: {
      id: 'demo-superadmin-001',
      email: 'admin@easylawdemo.com',
      full_name: 'Alex Morgan',
      role: 'super_admin',
      job_title: 'Managing Partner',
      department: 'Management',
      is_active: true,
      must_change_password: false,
    },
  },
  'lawyer@easylawdemo.com': {
    password: 'DemoLawyer123!',
    profile: {
      id: 'demo-lawyer-001',
      email: 'lawyer@easylawdemo.com',
      full_name: 'Jordan Clarke',
      role: 'lawyer',
      job_title: 'Senior Associate',
      department: 'Corporate & Commercial',
      is_active: true,
      must_change_password: false,
    },
  },
  'staff@easylawdemo.com': {
    password: 'DemoStaff123!',
    profile: {
      id: 'demo-staff-001',
      email: 'staff@easylawdemo.com',
      full_name: 'Sam Rivera',
      role: 'staff',
      job_title: 'Legal Administrator',
      department: 'Operations',
      is_active: true,
      must_change_password: false,
    },
  },
};

const SESSION_KEY = 'demo-auth-session';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { id: string; email: string };
        return parsed;
      }
    } catch { /* ignore */ }
    return null;
  });

  const [profile, setProfile] = useState<UserProfile | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { id: string; email: string };
        const demoEntry = Object.values(DEMO_USERS).find(u => u.profile.id === parsed.id);
        return demoEntry?.profile ?? null;
      }
    } catch { /* ignore */ }
    return null;
  });

  const [session, setSession] = useState<Session | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { id: string; email: string };
        return { user: parsed };
      }
    } catch { /* ignore */ }
    return null;
  });

  const [loading] = useState(false);

  const signIn = useCallback(async (email: string, password: string) => {
    const entry = DEMO_USERS[email.toLowerCase()];
    if (!entry || entry.password !== password) {
      throw new Error('Invalid email or password. Please use a demo account.');
    }
    const u: User = { id: entry.profile.id, email: entry.profile.email };
    setUser(u);
    setProfile(entry.profile);
    setSession({ user: u });
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(u));
    } catch { /* ignore */ }
  }, []);

  const signOut = useCallback(async () => {
    setUser(null);
    setProfile(null);
    setSession(null);
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch { /* ignore */ }
  }, []);

  const resetPassword = useCallback(async (_email: string) => {
    // No-op in demo mode
  }, []);

  const refreshProfile = useCallback(async () => {
    // Profile is static in demo mode
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, session, loading, signIn, signOut, resetPassword, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
